import { ChakraProvider, CSSReset } from '@chakra-ui/react'
import { ApiClient, ApiUser } from '@storefronts/common-types'
import type { AppProps } from 'next/app'
import Head from 'next/head'
import { useRouter } from 'next/router'
import posthog from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'
import { useEffect, useState } from 'react'
import theme from '../theme'
import { AuthLayout } from '../components/AuthLayout'
import { BasicLayout } from '../components/BasicLayout'
import { AuthContext } from '../modules/auth/authContext'
import { unprotectedRoutes } from '../modules/auth/unprotectedRoutes'
import Script from 'next/script'
import DatadogInit from '../components/DatadogInit'
import { PendoInit } from '../components/shared/PendoInit'
import { ThemeProvider } from '@/components/theme-provider'
import '../../styles/globals.css'
import { Toaster } from '@/components/ui/sonner'
import { OnboardingLayout } from '@/components/OnboardingLayout'
import { ChatlioInit } from '@/components/shared/Chatlio'
import { AnalyticsProvider } from '@/components/shared/AnalyticsProvider'

// Check that PostHog is client-side (used to handle Next.js SSR)
if (typeof window !== 'undefined') {
    const POSTHOG_API_KEY = process.env.NEXT_PUBLIC_API_POSTHOG_PUBLIC_KEY
    if (POSTHOG_API_KEY && window.location.hostname !== 'localhost') {
        posthog.init(POSTHOG_API_KEY, {
            api_host: 'https://app.posthog.com',
            // Enable debug mode in development
            loaded: (posthog) => {
                if (process.env.NODE_ENV === 'development') posthog.debug()
            },
        })
    }
}

function MyApp({ Component, pageProps }: AppProps) {
    const [user, setUser] = useState<null | ApiUser>(null)
    const [client, setClient] = useState<null | ApiClient>(null)
    const [isLoggedIn, setLoggedIn] = useState<null | boolean>(null)
    const [isSuperUser, setIsSuperUser] = useState(false)
    const router = useRouter()

    const state = {
        user,
        client,
        isLoggedIn,
        isSuperUser,
        setUser,
        setClient,
        setLoggedIn,
        setIsSuperUser,
    }

    useEffect(() => {
        // Track page views
        const handleRouteChange = () => posthog?.capture('$pageview')
        router.events.on('routeChangeComplete', handleRouteChange)

        return () => {
            router.events.off('routeChangeComplete', handleRouteChange)
        }
    }, [router.events])

    const isOnboardingRoute = router.pathname.includes('/onboarding')

    const Layout = unprotectedRoutes.includes(router.pathname)
        ? BasicLayout
        : isOnboardingRoute
          ? OnboardingLayout
          : AuthLayout

    return (
        <PostHogProvider client={posthog}>
            <ThemeProvider attribute="class" defaultTheme="light">
                <ChakraProvider theme={theme}>
                    <CSSReset />
                    <Head>
                        <link rel="shortcut icon" href="/favicon.png" />
                        <title>Storefronts</title>
                        <link
                            rel="preconnect"
                            href="https://fonts.googleapis.com"
                        ></link>
                        <link
                            rel="preconnect"
                            href="https://fonts.gstatic.com"
                            crossOrigin="anonymous"
                        ></link>
                        <link
                            href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Inter&display=swap"
                            rel="stylesheet"
                        ></link>
                        <link
                            href="https://fonts.googleapis.com/css2?family=Inter:wght@100;200;300;400;500;600&display=swap"
                            rel="stylesheet"
                        ></link>
                        <DatadogInit />
                    </Head>
                    <AuthContext.Provider value={state}>
                        <AnalyticsProvider>
                            <Layout>
                                <Component {...pageProps} />
                                <Toaster position="bottom-right" richColors />
                            </Layout>
                        </AnalyticsProvider>
                        <PendoInit />
                        <ChatlioInit />
                        <Script id="clarity-script" strategy="afterInteractive">
                            {`
            (function(c,l,a,r,i,t,y){
                c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
                t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
                y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
            })(window, document, "clarity", "script", "ocvmmqgf9f");
          `}
                        </Script>
                    </AuthContext.Provider>
                </ChakraProvider>
            </ThemeProvider>
        </PostHogProvider>
    )
}

export default MyApp
